import React, { useEffect, useRef, useState } from "react";
import { Volume2, Loader2, Pause } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Speaker button that fetches TTS audio from /api/tts and plays it via <audio>.
 * Props: text (required), lang ("en" | "es"), testid
 */
export function SpeakerButton({ text, lang = "en", testid = "speaker-btn" }) {
  const [state, setState] = useState("idle"); // idle | loading | playing
  const audioRef = useRef(null);
  const urlRef = useRef(null);

  useEffect(() => () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { /* noop */ } }
    if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch (e) { /* noop */ } }
  }, []);

  const stop = () => {
    if (audioRef.current) { try { audioRef.current.pause(); } catch (e) { /* noop */ } }
    setState("idle");
  };

  const play = async () => {
    if (state === "playing") return stop();
    setState("loading");
    try {
      const resp = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text, voice: lang === "es" ? "sage" : "sage" }),
      });
      if (!resp.ok) throw new Error(`TTS ${resp.status}`);
      const blob = await resp.blob();
      if (urlRef.current) { try { URL.revokeObjectURL(urlRef.current); } catch (e) { /* noop */ } }
      urlRef.current = URL.createObjectURL(blob);
      const audio = new Audio(urlRef.current);
      audioRef.current = audio;
      audio.onended = () => setState("idle");
      audio.onerror = () => { setState("idle"); toast.error("Could not play the audio."); };
      await audio.play();
      setState("playing");
    } catch (e) {
      console.error("tts failed", e);
      setState("idle");
      toast.error("Could not play the audio.");
    }
  };

  return (
    <button
      type="button"
      onClick={play}
      data-testid={testid}
      className="pod-focus inline-flex items-center gap-1.5 text-xs font-semibold text-[#4A525A] hover:text-[#C85A32] bg-white border border-[#E4DDD3] hover:border-[#C85A32] px-2.5 py-1 rounded-full transition-colors"
      aria-label={state === "playing" ? "Stop audio" : "Play audio"}
    >
      {state === "loading" && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {state === "playing" && <Pause className="w-3.5 h-3.5" />}
      {state === "idle" && <Volume2 className="w-3.5 h-3.5" />}
      {state === "playing" ? "Playing..." : state === "loading" ? "Loading..." : "Listen"}
    </button>
  );
}
